import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { parseApiError } from '../utils/apiError';
import type { StripeAccount } from '../types/stripe';

export function isStripeAccountReady(account?: StripeAccount | null): boolean {
  return (
    Boolean(account?.onboarding_completed) ||
    Boolean(account?.can_receive_payments) ||
    Boolean(account?.payouts_enabled)
  );
}

export function useStripeAccountStatus() {
  return useQuery<StripeAccount>({
    queryKey: ['stripe-account-status'],
    queryFn: async () => {
      try {
        const paymentSetupUrl = new URL(
          '/profile/settings/payments',
          window.location.origin,
        ).toString();
        const { data } = await apiClient.get('/custom_account/stripe/status/', {
          params: {
            return_url: paymentSetupUrl,
            refresh_url: paymentSetupUrl,
          },
        });
        return data;
      } catch (error) {
        if (parseApiError(error).status === 404) {
          // 404 means no Stripe account yet — return as valid not_created state
          return {
            status: 'not_created',
            next_action: 'create_account',
            onboarding_completed: false,
            can_receive_payments: false,
            charges_enabled: false,
            payouts_enabled: false,
            details_submitted: false,
          } as StripeAccount;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

export function useStartStripeOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (urls?: { returnUrl?: string; refreshUrl?: string }) => {
      const paymentSetupUrl = new URL(
        '/profile/settings/payments',
        window.location.origin,
      ).toString();
      const returnUrl = urls?.returnUrl || paymentSetupUrl;
      const refreshUrl = urls?.refreshUrl || returnUrl;
      const { data } = await apiClient.post('/custom_account/stripe/onboarding/', {
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-account-status'] });
    },
  });
}
